import { useCallback, useEffect, useState } from "react";
import type { MyReview, Stats } from "@coterie/types";
import { api, ApiError } from "./api.ts";
import { ReviewCard } from "./ReviewCard.tsx";

const counts = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function History() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsErr, setStatsErr] = useState(false);
  const [items, setItems] = useState<MyReview[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);

  const load = useCallback(async (next?: string) => {
    setError(null);
    if (next) setMore(true);
    else setLoading(true);
    try {
      const res = await api.myReviews({ cursor: next });
      setItems((prev) => (next ? [...prev, ...res.items] : res.items));
      setCursor(res.cursor);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        location.replace("/");
        return;
      }
      setError(e instanceof Error ? e.message : "couldn't load reviews");
    } finally {
      setLoading(false);
      setMore(false);
    }
  }, []);

  useEffect(() => {
    api
      .me()
      .then(() => {
        void api.stats().then(setStats).catch(() => setStatsErr(true));
        void load();
      })
      .catch(() => location.replace("/"));
  }, [load]);

  return (
    <div className="shell">
      <a className="ghost" href="/">
        ← review
      </a>
      <p className="lede">
        {stats
          ? `${counts(stats.reviewCount, "review", "reviews")} across ${counts(stats.venueCount, "café", "cafés")}`
          : statsErr
            ? "couldn't load stats"
            : "…"}
      </p>
      {error && (
        <>
          <p className="err">{error}</p>
          <button className="primary" onClick={() => void load()}>
            retry
          </button>
        </>
      )}
      {!error && loading && <p className="lede">loading…</p>}
      {!error && !loading && items.length === 0 && (
        <p className="lede">no reviews yet. take a photo.</p>
      )}
      {!error && items.length > 0 && (
        <ul className="history">
          {items.map((row) => (
            <ReviewCard key={`${row.createdAt}-${row.photoKey}`} {...row} />
          ))}
        </ul>
      )}
      {cursor && !error && (
        <button className="primary" disabled={more} onClick={() => void load(cursor)}>
          {more ? "loading…" : "load more"}
        </button>
      )}
    </div>
  );
}
